import {
  createContext,
  use,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type FormEvent,
  type RefObject,
} from 'react'
import {SidebarAgentFilterProvider} from '@github-ui/agent-sessions/contexts/SidebarAgentFilterContext'
import {TaskChat} from '@github-ui/agent-sessions/components/task-view/TaskChat'
import {useEnvironmentTaskLaunch} from '@github-ui/agent-sessions/hooks/use-environment-task-launch'
import {Banner, Button, FormControl, Textarea} from '@primer/react'
import {announce} from '@github-ui/aria-live'
import {createEnvironmentTaskEstablishedAhpSessionBootstrap} from '@github-ui/copilot-chat-app/runtime/mission-control'
import {chatSessionWithProjectForTaskId} from '@github-ui/copilot-chat-app/runtime/chat-history'
import type {EstablishedAhpSessionBootstrap} from '@github-ui/ahp-session-client/contracts'

import type {NormalizedCopilotAgentsTaskPayload} from '../types/copilot-agents-task-payload'
import {useRoutedTaskHandoff} from './use-routed-task-handoff'
import {useContributedInstructionsReady} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'
import {hpcAttrs} from '@github-ui/hpc'
import styles from './RegularTaskChat.module.css'

export const TaskNameChangeContext = createContext<((name: string | undefined) => void) | undefined>(undefined)

export function RegularTaskChat({
  payload,
  automationsVisible,
  deletionDestination,
  cliRemoteNoticeDismissed,
  repositoryContext,
}: {
  payload: NormalizedCopilotAgentsTaskPayload
  automationsVisible?: boolean
  deletionDestination?: string
  cliRemoteNoticeDismissed?: boolean
  repositoryContext?: {repoOwner: string; repoName: string}
}) {
  const onTaskNameChange = use(TaskNameChangeContext)
  const routedTaskId = payload.taskId ?? payload.task.id
  const instructionsReady = useContributedInstructionsReady()
  const launch = useEnvironmentTaskLaunch(routedTaskId, payload.task, {
    deferDestinationStartup: !instructionsReady,
  })
  const {
    persistentChat,
    isProvisioningThisTask,
    externallyProvisioned,
    hostOwnsEstablishment,
    establishedSessionPending,
    showOptimisticTurn,
    handleEstablishedAhpSession,
  } = useRoutedTaskHandoff(routedTaskId)
  const establishedAhpSession = hostOwnsEstablishment
    ? persistentChat?.establishedSession
    : launch.status === 'accepted'
      ? launch.session
      : undefined
  const sessionBindingFailed = persistentChat?.sessionBindingFailed === true
  const [retryBootstrap, setRetryBootstrap] = useState<EstablishedAhpSessionBootstrap>()
  const hasRenderedWithoutBindingFailureRef = useRef(false)
  const handleRetryBootstrapCreated = useCallback((bootstrap: EstablishedAhpSessionBootstrap) => {
    setRetryBootstrap(bootstrap)
  }, [])
  useEffect(() => () => retryBootstrap?.dispose(), [retryBootstrap])
  useLayoutEffect(() => {
    if (!sessionBindingFailed) hasRenderedWithoutBindingFailureRef.current = true
  }, [sessionBindingFailed])

  return (
    <SidebarAgentFilterProvider automationsVisible={automationsVisible}>
      <div className={styles.hpcContainer} {...hpcAttrs()}>
        <TaskChat
          key={routedTaskId}
          {...payload}
          taskId={routedTaskId}
          task={payload.task}
          onTaskNameChange={onTaskNameChange}
          directTurnTelemetrySurface="global_task"
          showHeader
          navigateOnDelete
          deletionDestination={deletionDestination}
          cliRemoteNoticeDismissed={cliRemoteNoticeDismissed}
          establishedAhpSession={establishedAhpSession}
          createdInCurrentView={
            isProvisioningThisTask || (launch.status !== 'unowned' && launch.status !== 'unchecked')
          }
          establishedAhpSessionPending={
            hostOwnsEstablishment
              ? establishedSessionPending || sessionBindingFailed
              : launch.status === 'pending' || launch.status === 'unchecked'
          }
          establishedAhpBootstrap={retryBootstrap ?? (launch.status === 'accepted' ? launch.bootstrap : undefined)}
          requiresEstablishedAhpBootstrap={
            (hostOwnsEstablishment && !sessionBindingFailed) ||
            (launch.status !== 'unowned' && launch.status !== 'unchecked')
          }
          requiredBootstrapError={launch.status === 'failed' || launch.status === 'expired' ? launch.error : undefined}
          provisioningPrompt={
            showOptimisticTurn
              ? persistentChat?.provisioningPrompt
              : launch.status === 'pending'
                ? launch.prompt
                : undefined
          }
          isProvisioning={isProvisioningThisTask}
          initialSelectedSpace={persistentChat?.initialSelectedSpace}
          provisioningDraft={persistentChat?.provisioningDraft}
          onProvisioningDraftChange={persistentChat?.onProvisioningDraftChange}
          restoredPrompt={persistentChat?.restoredPrompt}
          onRestoredPromptChange={persistentChat?.onRestoredPromptChange}
          onRestoredPromptAccepted={persistentChat?.onRestoredPromptAccepted}
          hideChatInput={sessionBindingFailed}
          onEnvironmentTaskLaunchAction={
            launch.status === 'failed' ? launch.retry : launch.status === 'expired' ? launch.restart : undefined
          }
          environmentTaskLaunchActionLabel={launch.status === 'expired' ? 'Restart startup' : undefined}
          {...(repositoryContext ? {repoOwner: repositoryContext.repoOwner, repoName: repositoryContext.repoName} : {})}
          {...(externallyProvisioned
            ? {
                provisioningPrompt: showOptimisticTurn ? persistentChat?.provisioningPrompt : undefined,
                provisioningPromptMode: 'until-transcript' as const,
                onEstablishedAhpSession: handleEstablishedAhpSession,
              }
            : {})}
        />
        {sessionBindingFailed && persistentChat.chatApiURL && persistentChat.retryPrompt ? (
          <SessionBindingRetry
            taskId={routedTaskId}
            apiURL={persistentChat.chatApiURL}
            prompt={persistentChat.restoredPrompt ?? persistentChat.retryPrompt.text}
            retryPrompt={persistentChat.retryPrompt}
            failureMessage={persistentChat.submissionFailure?.message}
            focusOnMountRef={hasRenderedWithoutBindingFailureRef}
            configureSession={persistentChat.configureRetrySession}
            onPromptChange={persistentChat.onRestoredPromptChange}
            onBootstrapCreated={handleRetryBootstrapCreated}
            onSessionEstablished={persistentChat.onRetrySessionEstablished}
          />
        ) : null}
      </div>
    </SidebarAgentFilterProvider>
  )
}

function SessionBindingRetry({
  taskId,
  apiURL,
  prompt,
  retryPrompt,
  failureMessage,
  focusOnMountRef,
  configureSession,
  onPromptChange,
  onBootstrapCreated,
  onSessionEstablished,
}: {
  taskId: string
  apiURL: string
  prompt: string
  retryPrompt: NonNullable<ReturnType<typeof useRoutedTaskHandoff>['persistentChat']>['retryPrompt']
  failureMessage?: string
  focusOnMountRef: RefObject<boolean>
  configureSession: NonNullable<ReturnType<typeof useRoutedTaskHandoff>['persistentChat']>['configureRetrySession']
  onPromptChange: NonNullable<ReturnType<typeof useRoutedTaskHandoff>['persistentChat']>['onRestoredPromptChange']
  onBootstrapCreated: (bootstrap: EstablishedAhpSessionBootstrap) => void
  onSessionEstablished: NonNullable<
    ReturnType<typeof useRoutedTaskHandoff>['persistentChat']
  >['onRetrySessionEstablished']
}) {
  const [retryText, setRetryText] = useState(prompt)
  const [retrying, setRetrying] = useState(false)
  const [retryError, setRetryError] = useState<string>()
  const retryInputRef = useRef<HTMLTextAreaElement>(null)
  const abortControllerRef = useRef<AbortController | undefined>(undefined)
  const retryBootstrapRef = useRef<EstablishedAhpSessionBootstrap | undefined>(undefined)
  useLayoutEffect(() => {
    if (!focusOnMountRef.current || document.activeElement !== document.body) return
    retryInputRef.current?.focus()
  }, [focusOnMountRef])
  useEffect(
    () => () => {
      abortControllerRef.current?.abort()
      retryBootstrapRef.current?.dispose()
    },
    [],
  )

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const text = retryText
    if (!text.trim() || retrying) return
    const taskChatRoot = event.currentTarget.parentElement?.previousElementSibling
    abortControllerRef.current?.abort()
    retryBootstrapRef.current?.dispose()
    const abortController = new AbortController()
    abortControllerRef.current = abortController
    retryBootstrapRef.current = undefined
    setRetrying(true)
    setRetryError(undefined)
    try {
      const {ahpSession, project} = await chatSessionWithProjectForTaskId(taskId, {
        apiURL,
        signal: abortController.signal,
      })
      if (abortController.signal.aborted) return
      const bootstrap = createEnvironmentTaskEstablishedAhpSessionBootstrap({apiURL, project})
      retryBootstrapRef.current = bootstrap
      const session = await bootstrap.establish({
        mode: 'recover',
        identity: ahpSession,
        firstTurn: {
          requestId: crypto.randomUUID(),
          prompt: {...retryPrompt, text},
          origin: 'recovery',
          directTurnTelemetryContext: {
            surface: 'global_task',
            environmentId: ahpSession.environmentId,
            taskId: ahpSession.taskId,
            sessionId: ahpSession.sessionId,
            repositoryBacked: Boolean(project),
          },
          ...(configureSession ? {configureSession} : {}),
        },
      })
      if (abortController.signal.aborted) return
      onBootstrapCreated(bootstrap)
      retryBootstrapRef.current = undefined
      onSessionEstablished?.(session, text)
      announce('Chat message sent.')
      requestAnimationFrame(() => {
        const target =
          taskChatRoot?.querySelector<HTMLElement>('textarea:not([disabled]):not([readonly])') ??
          taskChatRoot?.querySelector<HTMLElement>('[contenteditable="true"]') ??
          taskChatRoot?.querySelector<HTMLElement>('[role="region"][aria-label="Task activity"]')
        target?.focus()
      })
    } catch {
      if (abortController.signal.aborted) return
      retryBootstrapRef.current?.dispose()
      retryBootstrapRef.current = undefined
      setRetryError('Try again.')
      setRetrying(false)
      announce('Chat message could not be sent. Try again.', {assertive: true})
    } finally {
      if (abortControllerRef.current === abortController) abortControllerRef.current = undefined
    }
  }

  return (
    <div className="p-3 border-top">
      {retryError || failureMessage ? (
        <Banner variant="critical" title="Chat message could not be sent" description={retryError ?? failureMessage} />
      ) : null}
      <form onSubmit={handleSubmit}>
        <FormControl disabled={retrying}>
          <FormControl.Label>Retry chat message</FormControl.Label>
          <Textarea
            ref={retryInputRef}
            value={retryText}
            onChange={event => {
              setRetryText(event.target.value)
              onPromptChange?.(event.target.value)
            }}
            placeholder="Retry your message"
            block
            resize="vertical"
          />
        </FormControl>
        <Button type="submit" variant="primary" disabled={!retryText.trim()} loading={retrying} className="mt-2">
          Retry
        </Button>
      </form>
    </div>
  )
}
